/**
 * **一个纯 Pixi 的文本输入框。**
 *
 * ⚠️ 它**不是** DOM `<input>`，而且这不是偷懒 —— 是第 6 步实测之后的决定：
 * 页面上只要有可编辑元素获得焦点，输入法就会接管按键，中文状态下
 * `keydown` 收到的是 `'Process'`。所以这里自己拿 `keydown` 拼字符串。
 * 完整来龙去脉见 `ui/keys.ts`。
 *
 * 代价（明确记下）：**没有光标选区、没有中文输入**。
 * 剪贴板**支持粘贴**（房间号要能粘）—— 逐字符走和手打同一条过滤，
 * 仍然没有"选中一段再删"那种选区。昵称和房间号都是短英文串，够用。
 *
 * 状态反馈：hover 走 `state.ts` 的"加印一版"，**焦点**另画四角注册标记
 * （`docs/ui-design.md` §5）—— 而不是常见的"一圈虚线 focus ring"，
 * 那个是屏幕语言里最典型的装置。
 */

import { Container, Graphics } from 'pixi.js';
import { TextBuffer, type KeyEvent, type KeyHandler, type KeyRouter } from '../../ui/keys';
import { INK, TRACKING, uiLine, uiPad } from './style';
import { createSurface, drawRegistrationMarks, drawStamp, type ControlMotion, type ControlState } from './state';
import { stampInkAt } from './ink';
import { richLabel } from './richText';
import type { Widget } from './base';

export interface TextFieldSpec {
  readonly scale: number;
  readonly width: number;
  readonly keys: KeyRouter;
  readonly placeholder?: string;
  readonly maxLength: number;
  /** 逐字符过滤。房间号用它剔掉 I/L/O/0/1 */
  readonly accepts?: (ch: string) => boolean;
  readonly transform?: (value: string) => string;
  readonly onChange?: (value: string) => void;
  readonly onSubmit?: (value: string) => void;
  readonly onCancel?: () => void;
  /** 焦点在框里时按 Tab 会调它（在我们这儿 = 交给"确认加入"） */
  readonly onTab?: () => void;
  /** 初始值 */
  readonly value?: string;
  /**
   * **框内左侧的前缀**（纯视觉，不进 buffer）。房间号用它显示那个 #。
   *
   * ⚠️ 前缀不会变成 value 的一部分 —— 输入过滤、校验、提交拿到的都还是
   *    纯房间号（用户要求：输入逻辑完全不变）。
   */
  readonly prefix?: string;
  /**
   * **接管粘贴。**
   *
   * 给了它就**不再**走默认那条"逐字符塞进 buffer"的路，整段文本交给它
   * —— 房间号用它认出整条邀请链接（`https://…#ABCD`），否则默认过滤会把
   * `https` 吃成 `HTTP`。调用方在回调里自己 `setValue()`。
   */
  readonly onPaste?: (text: string) => void;
}

export interface TextField extends Widget {
  focus(): void;
  blur(): void;
  readonly focused: boolean;
  readonly value: string;
  setValue(value: string): void;
  /** 光标闪烁。挂到主循环上 —— 不调就是一根静止的光标 */
  update(deltaMS: number): void;
}

/** 光标闪一个来回要多久（毫秒） */
const CARET_PERIOD = 1060;

export function textField(spec: TextFieldSpec): TextField {
  const scale = spec.scale;
  const padX = uiPad(10, scale);
  const height = uiPad(34, scale);

  const buffer = new TextBuffer({
    maxLength: spec.maxLength,
    accepts: spec.accepts,
    transform: spec.transform,
  });
  if (spec.value !== undefined) buffer.value = spec.value;

  const container = new Container();
  const content = new Container();
  const background = new Graphics();
  // ★ 纯视觉前缀（房间号的 #）：不参与 buffer，只把文字和光标往右让。
  const prefixText =
    spec.prefix === undefined || spec.prefix === ''
      ? null
      : richLabel({ text: spec.prefix, scale, size: 15, fill: INK.textDim, role: 'mono' });
  const prefixW = prefixText === null ? 0 : prefixText.width + uiPad(4, scale);
  const text = richLabel({ text: '', scale, size: 15, fill: INK.text, role: 'mono', tracking: TRACKING.wide });
  const caret = new Graphics().rect(0, 0, uiLine(2, scale), 0).fill({ color: INK.primary });
  content.addChild(background);
  if (prefixText !== null) content.addChild(prefixText);
  content.addChild(text, caret);
  container.addChild(content);
  text.position.set(padX + prefixW, 0);

  let focused = false;
  let caretOn = true;
  let sinceBlink = 0;
  let release: (() => void) | null = null;

  const surface = createSurface({
    container,
    width: spec.width,
    height,
    cursor: 'text',
    onPress: () => focus(),
    onStateChange: (state, motion) => paint(state, motion),
    onFrame: (state, motion) => paint(state, motion),
  });

  function paint(state: ControlState, motion: ControlMotion): void {
    const ink = stampInkAt('secondary', state, motion.mix);
    const shown = buffer.value;

    background.clear();
    // 焦点：描边换成酸色（比 hover 的普通描边更"这块是当前字车位"）
    const usedInk = focused ? { ...ink, edge: INK.accent } : ink;
    drawStamp(background, usedInk, { width: spec.width, height }, scale, motion.lift);
    if (focused) {
      // 字车位标记：四角套准。替代常见的虚线 focus ring
      drawRegistrationMarks(background, { width: spec.width, height }, scale, INK.edgeStrong, motion.lift);
    }

    text.text = shown === '' && !focused ? (spec.placeholder ?? '') : shown;
    text.style.fill = shown === '' && !focused ? INK.textDim : INK.text;
    text.position.set(padX + prefixW, Math.round((height - text.height) / 2) + motion.lift);
    if (prefixText !== null) {
      prefixText.position.set(padX, Math.round((height - prefixText.height) / 2) + motion.lift);
    }

    // 光标紧跟在字后面。空的时候停在最左边 —— 不然"能打字"这件事看不出来
    const caretX = padX + prefixW + (shown === '' ? 0 : text.width + 2);
    const caretHeight = Math.max(uiPad(14, scale), text.height * 0.8);
    caret
      .clear()
      .rect(caretX, Math.round((height - caretHeight) / 2) + motion.lift, uiLine(2, scale), caretHeight)
      .fill({ color: INK.primary });
    caret.visible = focused && caretOn;
  }

  const handler: KeyHandler = {
    onKey(event: KeyEvent): boolean {
      if (event.key === 'Escape') {
        // ESC 先**失去焦点**，不直接退出这一屏 —— 不然改到一半手一抖就全丢了
        blur();
        spec.onCancel?.();
        return true;
      }
      if (event.key === 'Enter') {
        spec.onSubmit?.(buffer.value);
        return true;
      }
      if (event.key === 'Tab') {
        spec.onTab?.();
        return true;
      }
      if (buffer.handleKey(event.key)) {
        paint(surface.state, surface.motion);
        spec.onChange?.(buffer.value);
        return true;
      }
      // 吞掉可打印字符和退格：正在打字的时候，底下的界面不该收到这些键
      return event.key.length === 1 || event.key === 'Backspace';
    },
  };

  /**
   * **浏览器粘贴。** 我们不是一个 DOM 输入框，所以 `Ctrl/Cmd+V` 不会自动进来；
   * 只在获得焦点时监听 `paste`，把文本按和手打同一条规则塞进 buffer。
   */
  const onPaste = (event: ClipboardEvent): void => {
    if (!focused) return;
    const text = event.clipboardData?.getData('text') ?? '';
    if (text === '') return;
    event.preventDefault();
    if (spec.onPaste !== undefined) {
      spec.onPaste(text);
      return;
    }
    if (buffer.insert(text)) {
      paint(surface.state, surface.motion);
      spec.onChange?.(buffer.value);
    }
  };

  function focus(): void {
    if (focused) return;
    focused = true;
    caretOn = true;
    sinceBlink = 0;
    release = spec.keys.push(handler);
    window.addEventListener('paste', onPaste);
    paint(surface.state, surface.motion);
  }
  function blur(): void {
    if (!focused) return;
    focused = false;
    release?.();
    release = null;
    window.removeEventListener('paste', onPaste);
    paint(surface.state, surface.motion);
  }

  paint(surface.state, surface.motion);

  return {
    container,
    width: spec.width,
    height,
    get focused() {
      return focused;
    },
    get value() {
      return buffer.value;
    },
    focus,
    blur,
    setValue(next: string) {
      buffer.value = next;
      paint(surface.state, surface.motion);
      spec.onChange?.(buffer.value);
    },
    update(deltaMS: number) {
      if (!focused) return;
      sinceBlink += deltaMS;
      if (sinceBlink < CARET_PERIOD / 2) return;
      sinceBlink = 0;
      caretOn = !caretOn;
      caret.visible = caretOn;
    },
  };
}
